import {
  medalForStars,
  slotStarsForWagered,
  type SlotMedal,
} from '@goodtime/shared';
import { EventEmitter } from '../../core/Event';
import type { FenChatter, PlayerProfileSnapshot, ProfileStorage, StoryMode } from './ProfileStorage';

/** Seconds in a day — used for streak / claim cooldown maths. */
const SECONDS_PER_DAY = 86_400;
/** Daily bonus reward per streak day. Streak caps at 7 — day-7 grants
 * 700 coins, then day-8 wraps back to 1 if claimed continuously. */
const DAILY_BONUS_BY_STREAK = [0, 100, 200, 300, 400, 500, 600, 700];
const MAX_STREAK = DAILY_BONUS_BY_STREAK.length - 1; // 7

/** Fixed reward for the world-map daily-quest node. Independent of the
 * launch {@link PlayerProfile#claimDailyBonus} streak path — both can be
 * claimed once per 24h window without colliding. T6 (mini-game pass). */
const DAILY_QUEST_COIN_REWARD = 50;
const DAILY_QUEST_GEM_REWARD = 1;

/** Level curve — the climb from level (N-1) to level N costs
 * {@code N * XP_LEVEL_STEP} XP, so each level is steeper than the last
 * (a triangular curve). Reaching level N therefore costs a cumulative
 * {@code XP_LEVEL_STEP * N(N+1)/2} XP.
 *
 * <p>XP tracks cumulative wager (see {@link PlayerProfile.setBalance}), so
 * the previous flat 100-XP-per-level inflated levels ~1 per few spins — a
 * player crossed into the 20s inside a single session. The curve keeps the
 * first couple of levels within a new player's reach (level 1 is still 100
 * wagered) while making the higher levels take genuine playtime. Producer
 * 2026-06-10: "reduce the rate at which users level up much lower." Tune the
 * whole curve from this one knob. */
const XP_LEVEL_STEP = 100;

/** Gems awarded each time the player crosses a level threshold. Phase I
 * gem economy starter. With 5 gems/level + a 10-gem premium theme cost,
 * a player needs to reach ~level 2 to afford the first unlock — fits
 * comfortably inside a first-day session. */
const GEMS_PER_LEVEL = 5;

/**
 * Starter gem grant for a fresh profile. T1 gem economy (2026-05-28) —
 * matches the server-side {@code AddWalletService.INITIAL_GEMS} so the
 * solo (LocalStorage) and multiplayer (server-authoritative) paths
 * agree on first-launch generosity. Sized to cover the v1 paid-slot
 * catalogue (9 paid × 10 gems = 90 gems for full unlock) with 10 gems
 * left over — see {@code SLOT_UNLOCK_PRICE_GEMS} in shared for the
 * long-tail balancing rationale.
 */
const STARTER_GEM_GRANT = 100;

/** Auto-top-up when the player's coin balance hits zero — Goodtime
 *  Casino's "Infinite Coins · All the spin, none of the sting" promise.
 *  Mirrors the server-side {@code ZeroBalanceRefillPostProcessor}'s
 *  {@code REFILL_AMOUNT} so the solo (LocalStorageProfileStorage) and
 *  multiplayer (server-authoritative wallet) paths credit the same
 *  amount and the player's wallet ledger lines up regardless of where
 *  the refill fired. Bumped from 100 → 1000 on 2026-05-27 to match the
 *  v1 marketing claim "+1,000 — auto reload". */
const ZERO_BALANCE_TOP_UP = 1000;

/** Free themes that ship unlocked on every fresh profile. The five
 * commissioned biome themes (forest/underwater/frost/frontier/neon)
 * ship as the player's starter set so they're reachable from the
 * top-nav theme switcher on first launch — they ARE the v1
 * experience. Engine-bundled placeholder themes (default/midnight)
 * stay in the unlocked list as the "base art" reference. Premium
 * themes (e.g. "sunset") still have to be unlocked via the gem shop. */
const DEFAULT_UNLOCKED_THEMES = [
  'default',
  'midnight',
  'forest',
  'underwater',
  'frost',
  'frontier',
  'neon',
] as const;

/** Cumulative XP needed to REACH level {@code n} (0-indexed): the
 * triangular sum {@code XP_LEVEL_STEP * n(n+1)/2}. */
function cumulativeXpForLevel(n: number): number {
  return (XP_LEVEL_STEP * n * (n + 1)) / 2;
}

/** Translate cumulative XP into a level (0-indexed). Inverts the
 * triangular curve {@link cumulativeXpForLevel}: the largest {@code n}
 * with {@code n(n+1) <= 2*xp/STEP}. The {@code 1e-9} nudge stops exact
 * thresholds (e.g. 300 XP -> level 2) from flooring down on a sqrt
 * rounding error. */
export function levelForXp(xp: number): number {
  const x = Math.max(0, xp);
  return Math.floor(
    (-1 + Math.sqrt(1 + (8 * x) / XP_LEVEL_STEP)) / 2 + 1e-9,
  );
}

/** XP required to reach the *next* level, given current XP. */
export function xpToNextLevel(xp: number): number {
  const next = levelForXp(xp) + 1;
  return cumulativeXpForLevel(next) - Math.max(0, xp);
}

export type PlayerProfileEvent =
  | { kind: 'balance-changed'; before: number; after: number }
  | { kind: 'streak-changed'; before: number; after: number }
  | {
      kind: 'daily-bonus-claimed';
      /** Reward amount credited to balance. */
      reward: number;
      /** Streak day after the claim (1-indexed). */
      streak: number;
    }
  | { kind: 'xp-changed'; before: number; after: number }
  | {
      kind: 'level-up';
      /** Level reached after this XP gain (1-indexed). */
      level: number;
      /** XP value that crossed the threshold. */
      xp: number;
    }
  | { kind: 'onboarding-completed' }
  | { kind: 'gems-changed'; before: number; after: number }
  | {
      kind: 'theme-unlocked';
      themeId: string;
      /** Gems spent to unlock — for analytics + receipts. */
      cost: number;
    }
  | {
      /** T1 gem economy (2026-05-28) — fired after a successful slot
       *  purchase. Hosts listen to refresh the world-map unlock state
       *  + show a celebratory confirmation. The accompanying gem
       *  debit fires as a separate {@code gems-changed} event right
       *  before this. */
      kind: 'slot-unlocked';
      slotId: string;
      /** Gems spent — for analytics + receipts. {@code 0} for the
       *  free-by-default first-slot-per-biome case (in which the
       *  event still fires so listeners can re-render). */
      cost: number;
    }
  | {
      /** Favourite-slot toggle (2026-06-20). Fired by
       *  {@link PlayerProfile#toggleFavouriteSlot} when the player hearts /
       *  un-hearts a slot from the single-player HUD. Hosts mirror the
       *  favourites set into React state to refresh the heart fill + the
       *  favourites banner above the biome list. */
      kind: 'slot-favourite-changed';
      slotId: string;
      /** New state: true = now favourited, false = un-favourited. */
      favourited: boolean;
    }
  | {
      kind: 'daily-quest-claimed';
      coins: number;
      gems: number;
    }
  | {
      /** Slot mastery (M1, 2026-06-26) — fired by
       *  {@link PlayerProfile#recordWager} ONLY when a wager pushes a
       *  slot across a {@code STAR_THRESHOLDS} boundary (0→1, 1→2, 2→3).
       *  Sub-threshold wagers stay silent. Hosts listen to refresh the
       *  medal on the slot tile + play a "bronze star earned" toast.
       *  {@code slotId} is the stringified {@code WorldMapNode.id}, the
       *  same key the unlock + favourites economies use. */
      kind: 'slot-mastery-changed';
      slotId: string;
      /** Stars before / after this wager (0–3). {@code starsAfter >
       *  starsBefore} always holds — the event only fires on a gain. */
      starsBefore: number;
      starsAfter: number;
      /** Cumulative coins wagered on this slot after the wager. */
      wageredTotal: number;
    }
  | {
      kind: 'sound-pref-changed';
      before: boolean;
      after: boolean;
    }
  | {
      kind: 'music-pref-changed';
      before: boolean;
      after: boolean;
    }
  | {
      kind: 'sfx-pref-changed';
      before: boolean;
      after: boolean;
    }
  | {
      kind: 'gamble-pref-changed';
      before: boolean;
      after: boolean;
    }
  | {
      kind: 'bet-pref-changed';
      before: number;
      after: number;
    }
  | {
      /** Royal Road's story setting or Fen's chatter changed (Settings). */
      kind: 'story-pref-changed';
      storyMode: StoryMode;
      fenChatter: FenChatter;
    }
  | {
      /** The player opened a road slot for the first time. */
      kind: 'road-seen';
      themeId: string;
    }
  | {
      /** U12 M2/M4 (2026-05-31) — fired by the client sync hooks
       *  ({@code useSlotUnlockSync}, {@code useWalletSync}) when a
       *  server round-trip fails. Hosts mount a {@code SyncErrorBanner}
       *  that listens for this event and surfaces a dismissible toast
       *  ("Couldn't save your unlock", "Couldn't sync your gems") so
       *  the player isn't left wondering why a confirmed-locally action
       *  doesn't survive the next session.
       *
       *  <p>Informational only — the profile's local state stays
       *  authoritative for the immediate UX. The server reconciles on
       *  the next successful sync. We don't roll back the local action
       *  (that's a worse UX than the silent divergence).
       *
       *  <p>{@code operation} identifies the failing surface so the
       *  banner copy can be specific. {@code message} is the short
       *  human-readable reason (HTTP status, network error name);
       *  shown in dev / logs and not in the banner. */
      kind: 'sync-error';
      operation: 'slot-unlock' | 'wallet-sync' | 'daily-bonus' | 'daily-quest';
      message: string;
    }
  | {
      /** Fires when the auto-reload safety net catches a zero balance —
       *  the player just drained to 0 and the wallet was topped back up
       *  to {@link ZERO_BALANCE_TOP_UP}. Hosts listen for this to play
       *  the {@code RefillBurst} celebration overlay; the actual
       *  balance change has already been broadcast via the immediately-
       *  preceding {@code balance-changed} event. */
      kind: 'zero-balance-refill';
      /** Coin amount credited. Currently a fixed {@link ZERO_BALANCE_TOP_UP}
       *  but parameterised so analytics / future variants can vary it. */
      amount: number;
      /** Balance BEFORE the refill, after the original debit. Always 0
       *  in the current implementation; included for completeness so
       *  consumers don't have to assume. */
      from: number;
      /** Balance AFTER the refill. Equals {@code from + amount}. */
      to: number;
    };

export interface PlayerProfileConfig {
  /** Coins balance at session start. Loaded from {@link ProfileStorage}
   * if present; otherwise this default is used. */
  initialBalance?: number;
  /** Persistent storage. When provided, the profile loads from it on
   * construction and saves on every state change. Omit for in-memory
   * sessions (tests, headless). */
  storage?: ProfileStorage;
}

/** The retired app-side finds as a snapshot carried them (see
 *  {@link PlayerProfile}'s {@code _legacyFinds}). */
interface LegacyFinds {
  readonly collectedArtefactIds: readonly string[];
  readonly specialItemIds: readonly string[];
  readonly unlockedBiomeIds: readonly string[];
}

/** A snapshot's old finds, unchanged (empty for a new profile or a snapshot
 *  written before a field existed). */
function legacyFindsOf(snapshot: PlayerProfileSnapshot | null): LegacyFinds {
  return {
    collectedArtefactIds: [...(snapshot?.collectedArtefactIds ?? [])],
    specialItemIds: [...(snapshot?.specialItemIds ?? [])],
    unlockedBiomeIds: [...(snapshot?.unlockedBiomeIds ?? [])],
  };
}

/**
 * Cross-scene wallet — the single source of truth for the player's coin
 * balance, login state, and streak counter.
 *
 * <p>Phase G.1 added cross-scene balance. Phase G.2 adds:
 * <ul>
 *   <li>{@link lastLoginEpoch} + {@link loginStreak} — track consecutive
 *       daily claims;</li>
 *   <li>{@link claimDailyBonus} — call-once-per-day method that increments
 *       the streak (or resets it if a day was missed) and credits the
 *       streak-day reward;</li>
 *   <li>{@link ProfileStorage} integration — snapshot every state change
 *       so the wallet survives app restarts.</li>
 * </ul>
 *
 * <p>Phase D will replace {@link ProfileStorage} with a server-side
 * Wallet API; the profile shape stays the same.
 */
export class PlayerProfile {
  public readonly events = new EventEmitter<PlayerProfileEvent>();
  private readonly storage: ProfileStorage | null;
  private _balance: number;
  private _lastLoginEpoch: number;
  private _loginStreak: number;
  private _xp: number;
  private _onboardingComplete: boolean;
  private _gems: number;
  private _unlockedThemes: Set<string>;
  /** T1 — slot ids the player has purchased with gems. Free-by-default
   * slots aren't stored here; they're derived from
   * {@code FREE_SLOT_NODE_IDS} in the shared registry. */
  private _unlockedSlots: Set<string>;
  /** The retired app-side finds (October 2026): the artefacts, special
   *  items and mystery-world unlocks a player collected before relics
   *  replaced them. Nothing adds to them any more and nothing shows them;
   *  they are kept exactly as loaded and written back in every snapshot, so
   *  older app builds still read them (ADR-0011: fields are never removed). */
  private _legacyFinds: LegacyFinds;
  /** Slot node ids the player has favourited (2026-06-20). Drives the
   *  favourites banner above the biome list + the heart fill on the
   *  single-player slot HUD. Persisted; survives sessions + syncs. */
  private _favouriteSlots: Set<string>;
  /** Slot mastery (M1, 2026-06-26) — stringified {@code WorldMapNode.id}
   *  → cumulative coins wagered on that slot. Stars + medal are derived
   *  from this via {@code slotStarsForWagered} / {@code medalForStars};
   *  storing the raw wagered total (not the derived stars) keeps the
   *  thresholds tunable without a backfill. Persisted; survives sessions. */
  private _slotWagered: Map<string, number>;
  private _lastDailyQuestEpoch: number;
  private _soundEnabled: boolean;
  /** Background music + ambient channel toggle (2026-06-04 producer
   *  brief). Independent of {@link _soundEnabled} so a player can keep
   *  SFX on while killing the looping music bed. */
  private _musicEnabled: boolean;
  /** Sound-effects channel toggle (2026-06-07). Independent of the
   *  master ({@link _soundEnabled}) + music ({@link _musicEnabled}), so
   *  a player can silence SFX while keeping music, or vice-versa. */
  private _sfxEnabled: boolean;
  /** Gamble feature toggle (2026-06-04 producer brief). When false the
   *  scene auto-collects winnings + spin continues immediately, no
   *  gamble button shown anywhere. */
  private _gambleEnabled: boolean;
  private _slotBetMultiple: number;
  private _storyMode: StoryMode;
  private _fenChatter: FenChatter;
  private _roadSlotsSeen: Set<string>;
  private _wheelRunCardSeen: boolean;
  /** 2026-05-31 follow-up — when true, the starter gem grant hasn't
   *  fired yet. Set on fresh profiles; cleared by
   *  {@link claimStarterGems}. Returning players from older snapshots
   *  default to false (they got their grant under the pre-deferral
   *  constructor) and {@link claimStarterGems} is a no-op for them. */
  private _pendingStarterGems: boolean;
  /** True while a server wallet that counts XP backs this profile. The
   *  server counts every paid bet as XP and credits the level-up gems itself,
   *  so {@link awardXp} then moves only the XP and fires {@code level-up}:
   *  the gems arrive with the spin response or the wallet sync. False
   *  (offline, or a server that doesn't report XP) keeps the local grant.
   *  Runtime state, not persisted. */
  private _serverWallet = false;

  constructor(config: PlayerProfileConfig | number = {}) {
    // Backward-compat: the Phase G.1 constructor took just an initial
    // balance number. Accept either shape.
    const cfg: PlayerProfileConfig =
      typeof config === 'number' ? { initialBalance: config } : config;
    this.storage = cfg.storage ?? null;

    const loaded = this.storage?.load() ?? null;
    if (loaded) {
      this._balance = loaded.balance;
      this._lastLoginEpoch = loaded.lastLoginEpoch;
      this._loginStreak = loaded.loginStreak;
      this._xp = loaded.xp;
      this._onboardingComplete = loaded.onboardingComplete;
      this._gems = loaded.gems;
      // Merge saved unlocks with the current default set. When the
      // default expanded (e.g. v1.x added the commissioned biome
      // themes to the starter set), existing profiles need to pick
      // up the new themes too — otherwise they'd be stuck with
      // whatever was unlocked when they first launched. Premium
      // unlocks acquired via the gem shop survive the merge.
      this._unlockedThemes = new Set([
        ...DEFAULT_UNLOCKED_THEMES,
        ...loaded.unlockedThemeIds,
      ]);
      // T1 — slot unlocks carry through from prior sessions.
      this._unlockedSlots = new Set(loaded.unlockedSlotIds);
      this._legacyFinds = legacyFindsOf(loaded);
      // {@code ?? []} keeps profiles written before favourites loadable.
      this._favouriteSlots = new Set(loaded.favouriteSlotIds ?? []);
      // {@code ?? {}} keeps profiles written before slot mastery loadable.
      this._slotWagered = new Map(Object.entries(loaded.slotWagered ?? {}));
      this._lastDailyQuestEpoch = loaded.lastDailyQuestEpoch;
      this._soundEnabled = loaded.soundEnabled;
      this._musicEnabled = loaded.musicEnabled;
      this._sfxEnabled = loaded.sfxEnabled;
      this._gambleEnabled = loaded.gambleEnabled;
      this._slotBetMultiple = loaded.slotBetMultiple ?? 1;
      this._storyMode = loaded.storyMode ?? 'ask';
      this._fenChatter = loaded.fenChatter ?? 'often';
      this._roadSlotsSeen = new Set(loaded.roadSlotsSeen ?? []);
      this._wheelRunCardSeen = loaded.wheelRunCardSeen === true;
      // 2026-05-31 follow-up — defaults to false for snapshots written
      // by code that predates this field; those profiles already got
      // their grant under the old constructor path.
      this._pendingStarterGems = loaded.pendingStarterGems === true;
    } else {
      this._balance = cfg.initialBalance ?? 1000;
      this._lastLoginEpoch = 0;
      this._loginStreak = 0;
      this._xp = 0;
      this._onboardingComplete = false;
      // 2026-05-31 follow-up — defer the {@link STARTER_GEM_GRANT}
      // until the player taps GOT IT on the "Gems unlock slots"
      // onboarding step (or skips the welcome modal). The grant
      // landing as a visible +100 💎 toast at the moment of teaching
      // is a stronger UX beat than starting with 100 gems silently
      // in the wallet. {@link claimStarterGems} fires the actual
      // grant. Multiplayer wallets still get their server-side
      // grant immediately from {@code AddWalletService.INITIAL_GEMS};
      // the eventual {@code useWalletSync} reconcile won't double-
      // credit because that path silently reconciles on the first
      // sync.
      this._gems = 0;
      this._pendingStarterGems = true;
      this._unlockedThemes = new Set(DEFAULT_UNLOCKED_THEMES);
      this._unlockedSlots = new Set();
      this._legacyFinds = legacyFindsOf(null);
      this._favouriteSlots = new Set();
      this._slotWagered = new Map();
      this._lastDailyQuestEpoch = 0;
      this._soundEnabled = true;
      this._musicEnabled = true;
      this._sfxEnabled = true;
      this._gambleEnabled = true;
      this._slotBetMultiple = 1;
      this._storyMode = 'ask';
      this._fenChatter = 'often';
      this._roadSlotsSeen = new Set();
      this._wheelRunCardSeen = false;
    }
  }

  /** 2026-05-31 follow-up — fire the deferred starter-gem grant. Called
   *  from the welcome modal's "Gems unlock slots" CTA so the player
   *  sees a visible +100 💎 toast at the moment they're taught what
   *  gems are for. Also fires on welcome-modal skip / dismiss so a
   *  player who never reaches the gems step still gets their grant.
   *
   *  <p>Idempotent: no-op when {@link _pendingStarterGems} is false.
   *  Defensive: if a useWalletSync silent reconcile already raised
   *  the balance to (or above) {@link STARTER_GEM_GRANT}, only fire
   *  the cosmetic {@code gems-changed} event so {@link GemDropToast}
   *  still plays — never double-credit. */
  claimStarterGems(): void {
    if (!this._pendingStarterGems) return;
    this._pendingStarterGems = false;
    if (this._gems >= STARTER_GEM_GRANT) {
      // Already credited (server-side sync raced ahead). Fire the
      // celebration event with a synthesised 0 → grant delta so the
      // toast still plays. Wallet balance unchanged.
      this.events.emit({
        kind: 'gems-changed',
        before: 0,
        after: STARTER_GEM_GRANT,
      });
    } else {
      const before = this._gems;
      this._gems = STARTER_GEM_GRANT;
      this.events.emit({
        kind: 'gems-changed',
        before,
        after: this._gems,
      });
    }
    this.persist();
  }

  /** Test/diagnostic accessor. {@code true} on fresh profiles until
   *  {@link claimStarterGems} fires. False on every snapshot loaded
   *  from before this field existed. */
  get pendingStarterGems(): boolean {
    return this._pendingStarterGems;
  }

  get gems(): number {
    return this._gems;
  }

  /** Snapshot of unlocked theme ids — defensive copy so callers can't
   * mutate the internal set. */
  get unlockedThemeIds(): readonly string[] {
    return Array.from(this._unlockedThemes);
  }

  hasUnlockedTheme(id: string): boolean {
    return this._unlockedThemes.has(id);
  }

  /** Award gems — emits {@code gems-changed} + persists. Used by level-up
   * grants and (eventually) IAP. */
  awardGems(amount: number): void {
    if (amount <= 0) return;
    const before = this._gems;
    this._gems += amount;
    this.events.emit({ kind: 'gems-changed', before, after: this._gems });
    this.persist();
  }

  /** Reconcile the gem balance to a server-authoritative value WITHOUT
   *  firing {@code gems-changed}. Used by {@link useWalletSync} on the
   *  first sync after mount: the player didn't earn the diff just now —
   *  it accumulated during a prior session — so the
   *  {@link GemDropToast} would be a misleading celebration. Subsequent
   *  syncs use {@link awardGems} so live gem credits still toast.
   *
   *  <p>Idempotent + safe at any time: a no-op when the value already
   *  matches, persists the new value otherwise. The current/new pair
   *  is logged for diagnostic tracing only. */
  reconcileGems(serverGems: number): void {
    if (serverGems < 0) return;
    if (serverGems === this._gems) return;
    this._gems = serverGems;
    this.persist();
  }

  /** Move the gem balance to a server value as a live change: a rise goes
   *  through {@link awardGems} (the pill updates and the gem toast plays), a
   *  drop through {@link spendGems}. Unlike {@link reconcileGems}, for use
   *  mid-session, where the HUD mirrors need the event. */
  adoptGems(serverGems: number): void {
    if (serverGems < 0) return;
    const diff = serverGems - this._gems;
    if (diff > 0) this.awardGems(diff);
    else if (diff < 0) this.spendGems(-diff);
  }

  /** Try to spend gems. Returns false if balance is insufficient (no-op
   * in that case). On success, emits {@code gems-changed} + persists. */
  spendGems(amount: number): boolean {
    if (amount <= 0) return true;
    if (this._gems < amount) return false;
    const before = this._gems;
    this._gems -= amount;
    this.events.emit({ kind: 'gems-changed', before, after: this._gems });
    this.persist();
    return true;
  }

  /** Try to unlock a theme by paying its gem cost. Returns true on
   * success (gems debited, theme added). Returns false if already
   * unlocked OR insufficient gems — no events fire on failure. */
  unlockTheme(themeId: string, costGems: number): boolean {
    if (this._unlockedThemes.has(themeId)) return false;
    if (!this.spendGems(costGems)) return false;
    this._unlockedThemes.add(themeId);
    this.events.emit({ kind: 'theme-unlocked', themeId, cost: costGems });
    this.persist();
    return true;
  }

  // ---- T1 — slot unlocks (2026-05-28) ----
  //
  // The per-slot unlock state lives here for solo play (LocalStorage)
  // and is mirrored from the server's {@code Wallet.unlockedSlotIds}
  // for multiplayer. Free-by-default slots are NOT stored — they're
  // derived from the shared registry at the call site. Callers that
  // need "is this slot playable" should use {@code isSlotUnlocked}
  // from {@code @goodtime/shared} which handles the derivation.

  /** Snapshot of purchased slot ids — defensive copy. Does NOT include
   *  the free-by-default first-slot-per-biome (those are derived). */
  get unlockedSlotIds(): readonly string[] {
    return Array.from(this._unlockedSlots);
  }

  /** Returns true if the player has explicitly purchased this slot.
   *  Free-by-default slots are NOT considered "unlocked" by this method
   *  — use {@code isSlotUnlocked} from {@code @goodtime/shared} for the
   *  full playable check that includes the free derivation. */
  hasUnlockedSlot(slotId: string): boolean {
    return this._unlockedSlots.has(slotId);
  }

  /** Try to unlock a slot by paying its gem cost. Returns true on
   *  success (gems debited, slot added). Returns false if already
   *  unlocked OR insufficient gems — no events fire on failure.
   *
   *  <p>{@code costGems=0} is valid and exists so the same code path
   *  can record a "free unlock" if a future content addition wants to
   *  promote a paid slot to free retroactively for grandfathered
   *  players. In practice the v1 callers pass the price from
   *  {@code slotGemPrice(nodeId)} in shared. */
  unlockSlot(slotId: string, costGems: number): boolean {
    if (this._unlockedSlots.has(slotId)) return false;
    if (costGems > 0 && !this.spendGems(costGems)) return false;
    this._unlockedSlots.add(slotId);
    this.events.emit({ kind: 'slot-unlocked', slotId, cost: costGems });
    this.persist();
    return true;
  }

  // ---- Slot mastery (M1, 2026-06-26) ----
  //
  // Per-slot cumulative coins WAGERED → 3 stars → bronze/silver/gold medal.
  // Keyed by the stringified WorldMapNode.id (same as the unlock /
  // favourites economies). Solo-play only for now (no server mirror yet);
  // the server-authoritative aggregate lands in a later milestone.

  /** Cumulative coins wagered on a slot. {@code 0} for slots never played. */
  slotWagered(slotId: string): number {
    return this._slotWagered.get(slotId) ?? 0;
  }

  /** Stars (0–3) earned on a slot, derived from cumulative wagered. */
  slotStars(slotId: string): 0 | 1 | 2 | 3 {
    return slotStarsForWagered(this.slotWagered(slotId));
  }

  /** Headline medal for a slot: none / bronze / silver / gold. */
  slotMedal(slotId: string): SlotMedal {
    return medalForStars(this.slotStars(slotId));
  }

  /**
   * Record coins wagered on a slot — called once per paid spin with the
   * bet amount. Adds to the slot's running total and, when the new total
   * crosses a star threshold, emits {@code slot-mastery-changed} so the
   * HUD can light the medal + toast. Sub-threshold wagers persist silently
   * (no event). No-op for non-positive amounts (free spins don't wager).
   *
   * <p>Reaching gold (the 3rd star) lights the medal only: the relic gold
   * mastery lands is the server's (it counts the slot's wagers itself).
   */
  recordWager(slotId: string, amount: number): void {
    if (amount <= 0) return;
    const before = this._slotWagered.get(slotId) ?? 0;
    const after = before + amount;
    this._slotWagered.set(slotId, after);
    const starsBefore = slotStarsForWagered(before);
    const starsAfter = slotStarsForWagered(after);
    if (starsAfter > starsBefore) {
      this.events.emit({
        kind: 'slot-mastery-changed',
        slotId,
        starsBefore,
        starsAfter,
        wageredTotal: after,
      });
    }
    this.persist();
  }

  // ---- Favourite slots (2026-06-20) ----
  //
  // A flat set of slot node ids the player has hearted from the
  // single-player slot HUD. Pure preference — no currency, no gating.
  // Surfaced in the favourites banner above the biome list. Keyed by the
  // same stringified {@code WorldMapNode.id} the unlock economy uses.

  /** Slot node ids the player has favourited. Defensive copy so callers
   *  can't mutate the internal set. */
  get favouriteSlotIds(): readonly string[] {
    return Array.from(this._favouriteSlots);
  }

  /** True if the player has favourited this slot. */
  isSlotFavourited(slotId: string): boolean {
    return this._favouriteSlots.has(slotId);
  }

  /**
   * Toggle a slot's favourite state. Returns the NEW state (true = now
   * favourited, false = un-favourited). Emits {@code slot-favourite-changed}
   * + persists either way so the change syncs and survives a reload.
   */
  toggleFavouriteSlot(slotId: string): boolean {
    const favourited = !this._favouriteSlots.has(slotId);
    if (favourited) this._favouriteSlots.add(slotId);
    else this._favouriteSlots.delete(slotId);
    this.events.emit({ kind: 'slot-favourite-changed', slotId, favourited });
    this.persist();
    return favourited;
  }

  get onboardingComplete(): boolean {
    return this._onboardingComplete;
  }

  /** Mark the first-launch tour as complete. Idempotent — calling
   * repeatedly is a no-op. Persists immediately. */
  markOnboardingComplete(): void {
    if (this._onboardingComplete) return;
    this._onboardingComplete = true;
    this.events.emit({ kind: 'onboarding-completed' });
    this.persist();
  }

  get xp(): number {
    return this._xp;
  }

  get level(): number {
    return levelForXp(this._xp);
  }

  /** XP remaining to reach the next level — useful for HUD progress bars. */
  get xpToNextLevel(): number {
    return xpToNextLevel(this._xp);
  }

  get balance(): number {
    return this._balance;
  }

  get lastLoginEpoch(): number {
    return this._lastLoginEpoch;
  }

  get loginStreak(): number {
    return this._loginStreak;
  }

  /** Set the absolute balance. No-op if unchanged. Emits
   * {@code balance-changed} + persists. Debits also auto-award XP equal
   * to the spent amount — XP tracks cumulative wager, not net win/loss,
   * so a player's level grows with playing time regardless of luck.
   *
   * <p>Zero-balance safety net: when the value lands at or below 0
   * (player bust), top up to {@link ZERO_BALANCE_TOP_UP} so they can
   * keep playing — this is the Goodtime Casino "Infinite Coins · auto
   * reload" promise. Fires {@code zero-balance-refill} after the
   * balance-change so the UI can play the {@code RefillBurst} overlay.
   * Mirrors the server-side {@code ZeroBalanceRefillPostProcessor} so
   * solo and multiplayer paths credit identically. */
  setBalance(value: number): void {
    if (value === this._balance) return;
    let next = value;
    const refillFired = value <= 0;
    if (refillFired) next = ZERO_BALANCE_TOP_UP;
    if (next === this._balance) return;
    const before = this._balance;
    const delta = next - this._balance;
    this._balance = next;
    this.events.emit({ kind: 'balance-changed', before, after: next });
    if (delta < 0) {
      // Bet / debit — award XP equal to the wager amount.
      this.awardXp(-delta);
    }
    if (refillFired) {
      // The auto-reload triggered. Emit AFTER balance-changed so any
      // HUD listening for balance updates has already settled to the
      // new value by the time the celebration overlay reads state.
      this.events.emit({
        kind: 'zero-balance-refill',
        amount: ZERO_BALANCE_TOP_UP,
        from: 0,
        to: ZERO_BALANCE_TOP_UP,
      });
    }
    this.persist();
  }

  /** Reconcile the coin balance to a server-authoritative value WITHOUT
   *  the wager side effects of {@link setBalance}. The coin analogue of
   *  {@link reconcileGems}.
   *
   *  <p>The cold first sync in {@code useWalletSync} corrects the local
   *  placeholder balance (the {@code initialBalance}, e.g. 1000) down to
   *  the player's real server balance (e.g. 500). Routing that correction
   *  through {@link setBalance} would misread the downward delta as a
   *  wager and {@code awardXp(delta)} for it — minting phantom XP, phantom
   *  level-ups, and the {@link GEMS_PER_LEVEL} level-up gems that ride
   *  along. That is exactly the boot "balance drops + level/gems inflate
   *  on first spin" bug. This setter just snaps the value: emits
   *  {@code balance-changed} so the HUD mirror updates + persists, but
   *  awards no XP and never triggers the zero-balance refill. No-op when
   *  the value already matches; ignores negative inputs. */
  reconcileBalance(value: number): void {
    if (value < 0) return;
    if (value === this._balance) return;
    const before = this._balance;
    this._balance = value;
    this.events.emit({ kind: 'balance-changed', before, after: value });
    this.persist();
  }

  /** Synthetic auto-reload event — fires the {@code zero-balance-refill}
   *  event WITHOUT touching the wallet balance. Used by the first-session
   *  onboarding demo so every new install sees the brand-promise overlay
   *  within 60s of launch, regardless of whether they've actually busted
   *  yet. Mirrors the natural-bust event shape so any UI listening for
   *  refills (e.g. the {@code RefillBurst} overlay) reacts identically
   *  to the synthetic and real trigger.
   *
   *  <p>Deliberately not a "force a real refill at 1,000": new players
   *  already start with the full starting balance, so an actual top-up
   *  would either no-op (already at the cap) or grant excess coins. The
   *  demo is purely about teaching the brand promise — the visual is
   *  the message. */
  triggerRefillDemo(): void {
    this.events.emit({
      kind: 'zero-balance-refill',
      amount: ZERO_BALANCE_TOP_UP,
      from: 0,
      to: ZERO_BALANCE_TOP_UP,
    });
  }

  /** See {@link _serverWallet}. The wallet sync sets it when the server's
   *  wallet reports XP. */
  setServerWallet(attached: boolean): void {
    this._serverWallet = attached;
  }

  /** Adopt the server's XP (cumulative coins wagered, which the server
   *  counts on every paid spin and wheel-room bet). Awards no gems: the
   *  server credits those. Emits {@code xp-changed} when the value moves.
   *  With {@code announce}, a rise that crosses a level also fires
   *  {@code level-up} (a spin's level that the local count missed, e.g. after
   *  a Sweet Trail prize hid the bet from {@link setBalance}); the boot sync
   *  passes false, like {@link reconcileGems}, so a level gained on another
   *  device doesn't celebrate on launch. */
  reconcileXp(serverXp: number, announce = false): void {
    if (!Number.isFinite(serverXp) || serverXp < 0) return;
    if (serverXp === this._xp) return;
    const beforeXp = this._xp;
    const beforeLevel = this.level;
    this._xp = serverXp;
    this.events.emit({ kind: 'xp-changed', before: beforeXp, after: serverXp });
    if (announce && this.level > beforeLevel) {
      this.events.emit({ kind: 'level-up', level: this.level, xp: serverXp });
    }
    this.persist();
  }

  /** Direct XP grant — used by daily bonus + future achievement hooks.
   * Auto-fires {@code level-up} on a level threshold crossing AND, offline,
   * awards {@link GEMS_PER_LEVEL} gems for each level crossed (Phase I gem
   * economy). With a server wallet the server credits those gems. */
  awardXp(amount: number): void {
    if (amount <= 0) return;
    const beforeXp = this._xp;
    const beforeLevel = this.level;
    this._xp += amount;
    const afterLevel = this.level;
    this.events.emit({ kind: 'xp-changed', before: beforeXp, after: this._xp });
    if (afterLevel > beforeLevel) {
      this.events.emit({ kind: 'level-up', level: afterLevel, xp: this._xp });
      // Multi-level XP grants (e.g. a single big bonus) award gems for
      // every level crossed, not just one.
      const levelsCrossed = afterLevel - beforeLevel;
      if (!this._serverWallet) this.awardGems(GEMS_PER_LEVEL * levelsCrossed);
    }
  }

  /** Add to the running balance — convenience for cross-game credits. */
  credit(amount: number): void {
    if (amount <= 0) return;
    this.setBalance(this._balance + amount);
  }

  /** Subtract from the running balance — symmetric helper. */
  debit(amount: number): void {
    if (amount <= 0) return;
    this.setBalance(Math.max(0, this._balance - amount));
  }

  /**
   * Returns true iff the player is eligible to claim today's daily bonus.
   * "Today" is rolling — claims are once-per-24-hour-window from the
   * previous claim, NOT calendar-day-aligned, so a player who plays every
   * evening still gets their streak.
   */
  canClaimDailyBonus(now: number = nowEpoch()): boolean {
    if (this._lastLoginEpoch === 0) return true;
    return now - this._lastLoginEpoch >= SECONDS_PER_DAY;
  }

  /**
   * Claim today's daily bonus. No-op (returns null) if the player has
   * already claimed in the last 24h. Otherwise:
   * <ul>
   *   <li>If less than 48h passed since the previous claim, the streak
   *       advances by 1 (capped at 7).</li>
   *   <li>If more than 48h passed, the streak resets to 1.</li>
   *   <li>Reward = {@link DAILY_BONUS_BY_STREAK}[streak] coins.</li>
   *   <li>Emits {@code streak-changed}, {@code balance-changed}, then
   *       {@code daily-bonus-claimed}.</li>
   * </ul>
   *
   * <p>{@code now} is injectable for tests; defaults to the live wall clock.
   */
  claimDailyBonus(now: number = nowEpoch()): number | null {
    if (!this.canClaimDailyBonus(now)) return null;
    const beforeStreak = this._loginStreak;
    let nextStreak: number;
    if (this._lastLoginEpoch === 0) {
      // First claim ever.
      nextStreak = 1;
    } else {
      const elapsed = now - this._lastLoginEpoch;
      // 24h–48h: streak continues. >48h: streak resets to 1.
      nextStreak = elapsed < SECONDS_PER_DAY * 2
        ? Math.min(MAX_STREAK, this._loginStreak + 1)
        : 1;
    }
    const reward = DAILY_BONUS_BY_STREAK[nextStreak] ?? 0;

    this._loginStreak = nextStreak;
    this._lastLoginEpoch = now;
    if (beforeStreak !== nextStreak) {
      this.events.emit({ kind: 'streak-changed', before: beforeStreak, after: nextStreak });
    }
    if (reward > 0) {
      this.credit(reward); // emits balance-changed + persists
    } else {
      this.persist();
    }
    this.events.emit({ kind: 'daily-bonus-claimed', reward, streak: nextStreak });
    return reward;
  }

  /**
   * Adopt a daily-bonus claim made on the server, which credits the coins to
   * its wallet and holds the streak. Takes the server's streak, claim time
   * and coin balance. The balance is set without the wager side effects of
   * {@link setBalance}: when the local value was ahead of the server, the
   * drop is a correction, not a bet, so it must not award XP.
   *
   * <p>{@code claimed = false} (already claimed in the last 24h, e.g. on
   * another device) still records the streak and time, so the gift button
   * hides, but emits no {@code daily-bonus-claimed}.
   */
  applyDailyBonusClaim(claim: {
    claimed: boolean;
    reward: number;
    streak: number;
    claimedAt: number;
    coins: number;
  }): void {
    const beforeStreak = this._loginStreak;
    this._loginStreak = claim.streak;
    this._lastLoginEpoch = claim.claimedAt;
    if (beforeStreak !== claim.streak) {
      this.events.emit({ kind: 'streak-changed', before: beforeStreak, after: claim.streak });
    }
    this.reconcileBalance(claim.coins); // emits balance-changed when it moves
    this.persist();
    if (claim.claimed) {
      this.events.emit({ kind: 'daily-bonus-claimed', reward: claim.reward, streak: claim.streak });
    }
  }

  /** Epoch seconds of the last daily-quest claim. {@code 0} means never.
   * Hosts read this to render a HH:MM countdown to the next claim. */
  get lastDailyQuestEpoch(): number {
    return this._lastDailyQuestEpoch;
  }

  /** Returns true iff the player can claim today's daily-quest reward.
   * Independent of {@link canClaimDailyBonus} — both sit on their own
   * 24h cooldowns and don't interfere. */
  canClaimDailyQuest(now: number = nowEpoch()): boolean {
    if (this._lastDailyQuestEpoch === 0) return true;
    return now - this._lastDailyQuestEpoch >= SECONDS_PER_DAY;
  }

  /**
   * Claim the world-map daily-quest reward — fixed
   * {@link DAILY_QUEST_COIN_REWARD} coins + {@link DAILY_QUEST_GEM_REWARD}
   * gems on a 24h cooldown. Returns the credited reward, or {@code null}
   * if still on cooldown. Emits {@code balance-changed},
   * {@code gems-changed}, then {@code daily-quest-claimed}.
   *
   * <p>{@code now} is injectable for tests; defaults to the live wall clock.
   */
  claimDailyQuest(now: number = nowEpoch()): { coins: number; gems: number } | null {
    if (!this.canClaimDailyQuest(now)) return null;
    this._lastDailyQuestEpoch = now;
    this.credit(DAILY_QUEST_COIN_REWARD); // emits balance-changed + persists
    this.awardGems(DAILY_QUEST_GEM_REWARD); // emits gems-changed + persists
    this.events.emit({
      kind: 'daily-quest-claimed',
      coins: DAILY_QUEST_COIN_REWARD,
      gems: DAILY_QUEST_GEM_REWARD,
    });
    return { coins: DAILY_QUEST_COIN_REWARD, gems: DAILY_QUEST_GEM_REWARD };
  }

  /**
   * Adopt a daily-quest claim made on the server, which credits the coins
   * and the gem to its wallet and holds the cooldown. Takes the server's
   * claim time, coins and gems. Coins are set without the wager side effects
   * of {@link setBalance}; gems move as a live change ({@link adoptGems}).
   *
   * <p>{@code claimed = false} (already claimed in the last 24h, e.g. on
   * another device) still records the claim time, so the node shows its
   * cooldown, but emits no {@code daily-quest-claimed}.
   */
  applyDailyQuestClaim(claim: {
    claimed: boolean;
    coins: number;
    gems: number;
    claimedAt: number;
    walletCoins: number;
    walletGems: number;
  }): void {
    this._lastDailyQuestEpoch = claim.claimedAt;
    this.reconcileBalance(claim.walletCoins); // emits balance-changed when it moves
    this.adoptGems(claim.walletGems); // emits gems-changed when it moves
    this.persist();
    if (claim.claimed) {
      this.events.emit({ kind: 'daily-quest-claimed', coins: claim.coins, gems: claim.gems });
    }
  }

  /** Coin reward the player would receive if they claimed right now —
   * used to render the claim button label before the player commits. */
  previewDailyBonus(now: number = nowEpoch()): number {
    if (!this.canClaimDailyBonus(now)) return 0;
    if (this._lastLoginEpoch === 0) return DAILY_BONUS_BY_STREAK[1] ?? 0;
    const elapsed = now - this._lastLoginEpoch;
    const previewStreak = elapsed < SECONDS_PER_DAY * 2
      ? Math.min(MAX_STREAK, this._loginStreak + 1)
      : 1;
    return DAILY_BONUS_BY_STREAK[previewStreak] ?? 0;
  }

  /** Snapshot for storage / debugging / server sync. */
  snapshot(): PlayerProfileSnapshot {
    return {
      balance: this._balance,
      lastLoginEpoch: this._lastLoginEpoch,
      loginStreak: this._loginStreak,
      xp: this._xp,
      onboardingComplete: this._onboardingComplete,
      gems: this._gems,
      unlockedThemeIds: Array.from(this._unlockedThemes),
      unlockedSlotIds: Array.from(this._unlockedSlots),
      collectedArtefactIds: this._legacyFinds.collectedArtefactIds,
      favouriteSlotIds: Array.from(this._favouriteSlots),
      slotWagered: Object.fromEntries(this._slotWagered),
      specialItemIds: this._legacyFinds.specialItemIds,
      unlockedBiomeIds: this._legacyFinds.unlockedBiomeIds,
      lastDailyQuestEpoch: this._lastDailyQuestEpoch,
      soundEnabled: this._soundEnabled,
      musicEnabled: this._musicEnabled,
      sfxEnabled: this._sfxEnabled,
      gambleEnabled: this._gambleEnabled,
      slotBetMultiple: this._slotBetMultiple,
      storyMode: this._storyMode,
      fenChatter: this._fenChatter,
      roadSlotsSeen: Array.from(this._roadSlotsSeen),
      wheelRunCardSeen: this._wheelRunCardSeen,
      pendingStarterGems: this._pendingStarterGems,
    };
  }

  /**
   * Replace the entire profile state from a snapshot — the inverse of
   * {@link snapshot}. Used by the server profile-sync to restore a backup
   * on a fresh device / reinstall (2026-06-09).
   *
   * <p>SILENT BY DESIGN: sets every field + persists to the local cache,
   * but emits NO events, so restoring N unlocked slots doesn't fire N
   * celebration toasts (or a phantom level-up).
   * Hosts refresh their React mirrors from the getters after calling this,
   * exactly as the wallet cold-boot reconcile already does. Intended for
   * the boot path only, before the player has interacted with a scene.
   *
   * <p>Theme-merge matches the constructor: restored unlocks union with the
   * current {@link DEFAULT_UNLOCKED_THEMES}, so a backup written before the
   * default set expanded still picks up the newer free themes.
   */
  hydrate(snapshot: PlayerProfileSnapshot): void {
    this._balance = snapshot.balance;
    this._lastLoginEpoch = snapshot.lastLoginEpoch;
    this._loginStreak = snapshot.loginStreak;
    this._xp = snapshot.xp;
    this._onboardingComplete = snapshot.onboardingComplete;
    this._gems = snapshot.gems;
    this._unlockedThemes = new Set([
      ...DEFAULT_UNLOCKED_THEMES,
      ...snapshot.unlockedThemeIds,
    ]);
    this._unlockedSlots = new Set(snapshot.unlockedSlotIds);
    this._legacyFinds = legacyFindsOf(snapshot);
    this._favouriteSlots = new Set(snapshot.favouriteSlotIds ?? []);
    this._slotWagered = new Map(Object.entries(snapshot.slotWagered ?? {}));
    this._lastDailyQuestEpoch = snapshot.lastDailyQuestEpoch;
    this._soundEnabled = snapshot.soundEnabled;
    this._musicEnabled = snapshot.musicEnabled;
    this._sfxEnabled = snapshot.sfxEnabled;
    this._gambleEnabled = snapshot.gambleEnabled;
    this._slotBetMultiple = snapshot.slotBetMultiple ?? 1;
    this._storyMode = snapshot.storyMode ?? 'ask';
    this._fenChatter = snapshot.fenChatter ?? 'often';
    this._roadSlotsSeen = new Set(snapshot.roadSlotsSeen ?? []);
    this._wheelRunCardSeen = snapshot.wheelRunCardSeen === true;
    this._pendingStarterGems = snapshot.pendingStarterGems === true;
    this.persist();
  }

  // ---- Sound preferences (Phase 4 settings cog) ----

  get soundEnabled(): boolean {
    return this._soundEnabled;
  }

  /** Toggle the master sound flag. Persists + emits {@code sound-pref-changed}
   *  so downstream listeners (AudioBus, react UI) can react. */
  setSoundEnabled(value: boolean): void {
    if (this._soundEnabled === value) return;
    const before = this._soundEnabled;
    this._soundEnabled = value;
    this.persist();
    this.events.emit({ kind: 'sound-pref-changed', before, after: value });
  }

  get musicEnabled(): boolean {
    return this._musicEnabled;
  }

  /** Toggle the music + ambient channel. Persists + emits
   *  {@code music-pref-changed}; App.tsx routes the event into
   *  {@code AudioBus.setMusicEnabled} so playing music fades out
   *  immediately on disable and the next scene mount starts again
   *  on enable. */
  setMusicEnabled(value: boolean): void {
    if (this._musicEnabled === value) return;
    const before = this._musicEnabled;
    this._musicEnabled = value;
    this.persist();
    this.events.emit({ kind: 'music-pref-changed', before, after: value });
  }

  get sfxEnabled(): boolean {
    return this._sfxEnabled;
  }

  /** Toggle the sound-effects channel. Persists + emits
   *  {@code sfx-pref-changed}; App.tsx routes it into
   *  {@code AudioBus.setSfxEnabled} so one-shot cues mute immediately. */
  setSfxEnabled(value: boolean): void {
    if (this._sfxEnabled === value) return;
    const before = this._sfxEnabled;
    this._sfxEnabled = value;
    this.persist();
    this.events.emit({ kind: 'sfx-pref-changed', before, after: value });
  }

  get gambleEnabled(): boolean {
    return this._gambleEnabled;
  }

  /** Toggle the gamble feature. When false the slot scene auto-
   *  collects on each win and the inline GAMBLE button is hidden;
   *  the win/spin loop becomes "winnings credit immediately, then
   *  next spin." */
  setGambleEnabled(value: boolean): void {
    if (this._gambleEnabled === value) return;
    const before = this._gambleEnabled;
    this._gambleEnabled = value;
    this.persist();
    this.events.emit({ kind: 'gamble-pref-changed', before, after: value });
  }

  /** The bet the player last chose, as a multiple of a slot's bet. */
  get slotBetMultiple(): number {
    return this._slotBetMultiple;
  }

  /** Remember the player's bet choice. Persists + emits
   *  {@code bet-pref-changed}, which the profile sync backs up, so the next
   *  slot (on any device) starts at the same multiple. */
  setSlotBetMultiple(value: number): void {
    if (!Number.isFinite(value) || value < 1 || this._slotBetMultiple === value) return;
    const before = this._slotBetMultiple;
    this._slotBetMultiple = value;
    this.persist();
    this.events.emit({ kind: 'bet-pref-changed', before, after: value });
  }

  /** Royal Road's story: `ask` until the player chooses, then `on` (Fen
   *  and the story cards) or `off` (Classic mode). */
  get storyMode(): StoryMode {
    return this._storyMode;
  }

  /** When Fen speaks up: big moments only, or often. */
  get fenChatter(): FenChatter {
    return this._fenChatter;
  }

  /** Change the story setting or Fen's chatter. Persists + emits
   *  {@code story-pref-changed}, which the profile sync backs up. */
  setStoryPrefs(prefs: { storyMode?: StoryMode; fenChatter?: FenChatter }): void {
    const storyMode = prefs.storyMode ?? this._storyMode;
    const fenChatter = prefs.fenChatter ?? this._fenChatter;
    if (storyMode === this._storyMode && fenChatter === this._fenChatter) return;
    this._storyMode = storyMode;
    this._fenChatter = fenChatter;
    this.persist();
    this.events.emit({ kind: 'story-pref-changed', storyMode, fenChatter });
  }

  /** Road slots (theme ids) the player has opened. */
  get roadSlotsSeen(): readonly string[] {
    return Array.from(this._roadSlotsSeen);
  }

  /** Note a road slot as opened. Persists + emits {@code road-seen} the
   *  first time, which the profile sync backs up. */
  markRoadSeen(themeId: string): void {
    if (this._roadSlotsSeen.has(themeId)) return;
    this._roadSlotsSeen.add(themeId);
    this.persist();
    this.events.emit({ kind: 'road-seen', themeId });
  }

  /** Whether the player has seen the card that says every slot has a
   *  Wheel Run now: it shows once. */
  get wheelRunCardSeen(): boolean {
    return this._wheelRunCardSeen;
  }

  /** The Wheel Run card has shown: it won't again. Persists. */
  markWheelRunCardSeen(): void {
    if (this._wheelRunCardSeen) return;
    this._wheelRunCardSeen = true;
    this.persist();
  }

  dispose(): void {
    this.events.clear();
  }

  private persist(): void {
    if (!this.storage) return;
    this.storage.save(this.snapshot());
  }
}

function nowEpoch(): number {
  return Math.floor(Date.now() / 1000);
}
